<script setup>
/* eslint-env browser */
/* global FB */
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { loadScript } from 'dashboard/helper/DOMHelpers';
import { buildFacebookLoginScopes } from 'dashboard/helper/facebookScopes';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import * as Sentry from '@sentry/vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const HUMAN_AGENT_SCOPE = 'human_agent';

const { t } = useI18n();
const store = useStore();
const isRequesting = ref(false);
const requestError = ref('');

const runFBInit = () => {
  FB.init({
    appId: window.chatwootConfig.fbAppId,
    xfbml: true,
    version: window.chatwootConfig.fbApiVersion,
    status: true,
  });
  window.fbSDKLoaded = true;
};

onMounted(() => {
  window.fbAsyncInit = runFBInit;
});

const reauthorizePage = async omniauthToken => {
  try {
    await store.dispatch('inboxes/reauthorizeFacebookPage', {
      omniauthToken,
      inboxId: props.inbox.id,
    });
    useAlert(t('INBOX_MGMT.HUMAN_AGENT_PERMISSION.SUCCESS'));
  } catch (error) {
    requestError.value = t('INBOX_MGMT.FACEBOOK_REAUTHORIZE.MESSAGE_ERROR');
  } finally {
    isRequesting.value = false;
  }
};

const tryFBLogin = () => {
  FB.login(
    response => {
      if (response.status === 'connected' && response.authResponse) {
        reauthorizePage(response.authResponse.accessToken);
        return;
      }
      isRequesting.value = false;
      requestError.value = t('INBOX_MGMT.HUMAN_AGENT_PERMISSION.NOT_GRANTED');
    },
    {
      scope: `${buildFacebookLoginScopes()},${HUMAN_AGENT_SCOPE}`,
      auth_type: 'rerequest',
    }
  );
};

const requestPermission = async () => {
  isRequesting.value = true;
  requestError.value = '';
  try {
    await loadScript('https://connect.facebook.net/en_US/sdk.js', {
      id: 'facebook-jssdk',
    });
    runFBInit();
    tryFBLogin();
  } catch (error) {
    isRequesting.value = false;
    Sentry.captureException(error);
    useAlert(t('INBOX_MGMT.DETAILS.ERROR_FB_LOADING'));
  }
};
</script>

<template>
  <SettingsSection
    :title="$t('INBOX_MGMT.HUMAN_AGENT_PERMISSION.TITLE')"
    :sub-title="$t('INBOX_MGMT.HUMAN_AGENT_PERMISSION.SUBTITLE')"
  >
    <div class="flex flex-col items-start gap-3">
      <p v-if="requestError" class="text-sm text-n-ruby-9">
        {{ requestError }}
      </p>
      <p class="text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.HUMAN_AGENT_PERMISSION.PERMISSION_NOTE') }}
      </p>
      <NextButton
        :label="$t('INBOX_MGMT.HUMAN_AGENT_PERMISSION.BUTTON')"
        :is-loading="isRequesting"
        @click="requestPermission"
      />
    </div>
  </SettingsSection>
</template>
